import React, { useState, useEffect } from 'react';
import { Avatar, Skeleton } from '@mui/material';
import useGetPartnerProfile from '../../hooks/useGetPartnerProfile';
import Tables from '../../components/organisms/Tables/Tables';
import { useQuery } from 'react-query';
import { getProfileInvoices, downloadProfileInvoices, downloadMonthlyReport } from '../../hooks/usePartnerConfig';
import CurrencyAmount from '../../components/organisms/Tables/CellRenderer/CurrencyAmount';
import toast from 'react-hot-toast';
import { useProfile } from '../../context/ProfileContext';

interface ProfileProps {}

const columns = [
  // {
  //   id: 'invoiceName',
  //   label: 'Invoice Name',
  //   minWidth: 100,
  //   align: 'left',
  // },
  {
    id: 'date',
    label: 'Month',
    minWidth: 100,
    align: 'left',
  },
  { id: 'initialAmount', label: 'Gross Amount', minWidth: 100, align: 'left' },
  { id: 'gstPercentage', label: 'GST Amt (GST%)', minWidth: 100, align: 'left' },
  {
    id: 'tdsPercentage',
    label: 'TDS Amt (TDS%)',
    minWidth: 100,
    align: 'left',
  },
  {
    id: 'amountPaid',
    label: 'Net Amount',
    minWidth: 100,
    align: 'left',
  },
  { id: 'invoiceNumber', label: 'Invoice Number', minWidth: 170, align: 'left' },
  {
    id: 'signedURL',
    label: 'Download Invoice',
    minWidth: 100,
    align: 'left',
  },
  {
    id: 'payoutSummary',
    label: 'Payout Summary',
    minWidth: 100,
    align: 'left',
  },
];

const DownloadButton = React.memo(
  ({
    invoiceNumber,
    isLoading,
    onDownload,
  }: {
    invoiceNumber: string;
    isLoading: boolean;
    onDownload: (invoiceNumber: string) => void;
  }) => {
    return (
      <button
        className="border border-green-800 text-sm mx-auto text-green-800 rounded-lg font-semibold py-2 px-1 w-[130px] opacity-80 hover:opacity-100 hover:bg-green-800 hover:text-white transition-all duration-200 ease-in-out"
        onClick={(e) => {
          e.stopPropagation();
          onDownload(invoiceNumber);
        }}
        disabled={isLoading}
      >
        {isLoading ? 'Downloading...' : 'Download'}
      </button>
    );
  },
);

DownloadButton.displayName = 'DownloadButton';

const PayoutSummaryButton = React.memo(
  ({
    month,
    year,
    isLoading,
    isChecking,
    isDisabled,
    onDownload,
  }: {
    month: number;
    year: number;
    isLoading: boolean;
    isChecking: boolean;
    isDisabled: boolean;
    onDownload: (month: number, year: number) => void;
  }) => {
    const getButtonText = () => {
      if (isChecking) return 'Checking...';
      if (isLoading) return 'Loading...';
      if (isDisabled) return 'Not Available';
      return 'Payout Summary';
    };

    return (
      <button
        className={`border text-sm mx-auto rounded-lg font-semibold py-2 px-1 w-[130px] transition-all duration-200 ease-in-out ${
          isDisabled || isChecking
            ? 'border-gray-300 text-gray-400 cursor-not-allowed'
            : 'border-green-800 text-green-800 opacity-80 hover:opacity-100 hover:bg-green-800 hover:text-white'
        }`}
        onClick={(e) => {
          e.stopPropagation();
          if (!isDisabled && !isChecking) {
            onDownload(month, year);
          }
        }}
        disabled={isLoading || isDisabled || isChecking}
      >
        {getButtonText()}
      </button>
    );
  },
);

PayoutSummaryButton.displayName = 'PayoutSummaryButton';

const Profile: React.FC<ProfileProps> = () => {
  const [downloadingInvoices, setDownloadingInvoices] = useState<Record<string, boolean>>({});
  const [downloadingReports, setDownloadingReports] = useState<Record<string, boolean>>({});
  const [unavailableReports, setUnavailableReports] = useState<Record<string, boolean>>({});
  const [cachedReportUrls, setCachedReportUrls] = useState<Record<string, string>>({});
  const [checkingReports, setCheckingReports] = useState<Record<string, boolean>>({});
  const { data, isLoading: loadingPartnerProfile } = useGetPartnerProfile();

  const { profileData } = useProfile();

  const { profile } = data?.data || {};
  const { accountNumber, accountIfsc } = data?.data?.bankAccount
    ? data?.data?.bankAccount
    : { accountNumber: '', accountIfsc: '' };

  const { firstName, lastName, email, phone, panLinkedAadhaar, pan, gst } = profile || {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    panLinkedAadhaar: '',
    pan: '',
    gst: '',
  };

  let name = `${firstName} ${lastName}`;

  const handleDownload = React.useCallback(async (invoiceNumber: string) => {
    setDownloadingInvoices((prev) => ({ ...prev, [invoiceNumber]: true }));
    try {
      const res = await downloadProfileInvoices({ invoiceId: invoiceNumber });
      const link = document.createElement('a');
      link.href = res;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      const status = error.response?.status;

      if (status && status >= 300 && status < 500) {
        toast.dismiss();
        toast.error(error.response.data.message);
      } else {
        toast.dismiss();
        toast.error('Error downloading invoice');
      }
    } finally {
      setDownloadingInvoices((prev) => ({ ...prev, [invoiceNumber]: false }));
    }
  }, []);

  const checkReportAvailability = React.useCallback(async (month: number, year: number) => {
    const key = `${year}-${month}`;
    if (unavailableReports[key] || cachedReportUrls[key] || checkingReports[key]) {
      return;
    }
    setCheckingReports((prev) => ({ ...prev, [key]: true }));
    try {
      const res = await downloadMonthlyReport({ month, year });
      const downloadUrl = res?.data || res;
      if (downloadUrl) {
        setCachedReportUrls((prev) => ({ ...prev, [key]: downloadUrl }));
      }
    } catch (error) {
      const status = error.response?.status;
      if (status === 404) {
        setUnavailableReports((prev) => ({ ...prev, [key]: true }));
      }
    } finally {
      setCheckingReports((prev) => ({ ...prev, [key]: false }));
    }
  }, [unavailableReports, cachedReportUrls, checkingReports]);

  const handleDownloadReport = React.useCallback(async (month: number, year: number) => {
    const key = `${year}-${month}`;

    // If we have a cached URL, use it directly
    if (cachedReportUrls[key]) {
      const link = document.createElement('a');
      link.href = cachedReportUrls[key];
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      return;
    }

    setDownloadingReports((prev) => ({ ...prev, [key]: true }));
    try {
      const res = await downloadMonthlyReport({ month, year });
      const downloadUrl = res?.data || res;
      const link = document.createElement('a');
      link.href = downloadUrl;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      // Cache the URL for future use
      setCachedReportUrls((prev) => ({ ...prev, [key]: downloadUrl }));
    } catch (error) {
      const status = error.response?.status;

      if (status === 404) {
        setUnavailableReports((prev) => ({ ...prev, [key]: true }));
        toast.dismiss();
        toast.error('Payout summary not available for this month');
      } else if (status && status >= 300 && status < 500) {
        toast.dismiss();
        toast.error(error.response?.data?.message || 'Error downloading payout summary');
      } else {
        toast.dismiss();
        toast.error('Error downloading payout summary');
      }
    } finally {
      setDownloadingReports((prev) => ({ ...prev, [key]: false }));
    }
  }, [cachedReportUrls]);

  const getRowsAndCols = (data: any[]) => {
    return data?.map((element) => {
      const invoiceDate = new Date(element.date);
      const month = invoiceDate.getMonth() + 1;
      const year = invoiceDate.getFullYear();
      const reportKey = `${year}-${month}`;

      return {
        date: invoiceDate.toLocaleString('default', { month: 'long', year: 'numeric' }),
        initialAmount: (
          <CurrencyAmount amount={element.initialAmount} key={`amount-${element.invoiceNumber}`} />
        ),
        gstPercentage: isNaN(
          parseFloat(((element.initialAmount * element.gstPercentage) / 100).toFixed(2)),
        )
          ? 'N/A'
          : parseFloat(((element.initialAmount * element.gstPercentage) / 100).toFixed(2)) +
            ' (' +
            element.gstPercentage.toFixed(2) +
            '%)',
        tdsPercentage: isNaN(
          parseFloat(((element.initialAmount * element.tdsPercentage) / 100).toFixed(2)),
        )
          ? 'N/A'
          : parseFloat(((element.initialAmount * element.tdsPercentage) / 100).toFixed(2)) +
            ' (' +
            element.tdsPercentage.toFixed(2) +
            '%)',
        amountPaid: (
          <CurrencyAmount
            amount={element.amountPaid}
            key={`paid-${element.invoiceNumber}`}
            customStyle={{ fontWeight: '600' }}
          />
        ),
        invoiceNumber: element.invoiceNumber,

        signedURL: (
          <DownloadButton
            key={`download-${element.invoiceNumber}`}
            invoiceNumber={element.invoiceNumber}
            isLoading={downloadingInvoices[element.invoiceNumber]}
            onDownload={handleDownload}
          />
        ),
        payoutSummary: (
          <PayoutSummaryButton
            key={`payout-${element.invoiceNumber}`}
            month={month}
            year={year}
            isLoading={downloadingReports[reportKey]}
            isChecking={checkingReports[reportKey]}
            isDisabled={unavailableReports[reportKey]}
            onDownload={handleDownloadReport}
          />
        ),
      };
    });
  };

  const { data: profileInvoices } = useQuery('profileInvoices', getProfileInvoices);

  // Check report availability for all invoices when they load
  useEffect(() => {
    if (profileInvoices && profileInvoices.length > 0) {
      const uniqueMonthYears = new Set<string>();
      profileInvoices.forEach((invoice: any) => {
        const date = new Date(invoice.date);
        const month = date.getMonth() + 1;
        const year = date.getFullYear();
        uniqueMonthYears.add(`${month}-${year}`);
      });

      uniqueMonthYears.forEach((key) => {
        const [month, year] = key.split('-').map(Number);
        checkReportAvailability(month, year);
      });
    }
  }, [profileInvoices, checkReportAvailability]);

  const rows = getRowsAndCols(profileInvoices || []);

  return (
    <>
      {/* User Details */}
      <div className="my-6 max-w-7xl mx-auto ">
        {profileData?.features?.enableProfileDetails && (
          <div className="bg-black/[0.04] min-h-20 grid lg:grid-cols-4 grid-cols-3 gap-6 sm:gap-3 lg:gap-2 rounded-lg px-6 py-4 mt-6">
            {/* Name/PAN */}
            <div className="col-span-3 max-lg:mb-6 lg:col-span-1 flex items-center gap-3">
              {firstName && (
                <Avatar
                  sizes=""
                  className="uppercase md:!w-14 md:!h-14 !bg-white/80 !text-slate-600 border border-slate-300"
                >
                  {firstName?.charAt(0)}
                </Avatar>
              )}
              <div>
                <h3 className="text-slate-800 mb-0.5 font-medium">
                  {loadingPartnerProfile ? (
                    <Skeleton width={50} />
                  ) : name.length < 12 ? (
                    name
                  ) : (
                    name.slice(0, 12) + '...'
                  )}
                </h3>
                <h4 className="text-stone-500 text-sm font-medium tracking-widest">{pan || '-'}</h4>
              </div>
            </div>
            {/* Email ID */}
            <div className="lg:text-right col-span-1 lg:col-span-1">
              <h3 className="text-stone-500 font-semibold text-xs uppercase mb-1">Email ID</h3>
              <h4 className="text-sm font-medium text-slate-800">
                {loadingPartnerProfile ? (
                  <Skeleton width={70} className="ml-auto" />
                ) : email.length <= 18 ? (
                  email
                ) : (
                  email.slice(0, 18) + '...'
                )}
              </h4>
            </div>
            {/* Phone */}
            <div className="lg:text-right col-span-1 lg:col-span-1">
              <h3 className="text-stone-500 font-semibold text-xs uppercase mb-1">Phone No.</h3>
              <h4 className="text-sm font-medium text-slate-800">
                {loadingPartnerProfile ? <Skeleton width={70} className="ml-auto" /> : phone || '-'}
              </h4>
            </div>
            {/* Aadhaar */}
            <div className="lg:text-right col-span-1 lg:col-span-1">
              <h3 className="text-stone-500 font-semibold text-xs uppercase mb-1">Aadhaar</h3>
              <h4 className="text-sm font-medium text-slate-800">
                {loadingPartnerProfile ? (
                  <Skeleton width={70} className="ml-auto" />
                ) : panLinkedAadhaar ? (
                  `**** **** ${panLinkedAadhaar}`
                ) : (
                  '-'
                )}
              </h4>
            </div>
            {/* GST */}

            <div className="lg:text-right col-span-1 lg:col-span-2">
              <h3 className="text-stone-500 font-semibold text-xs uppercase mb-1">GST Number</h3>
              <h4 className="text-sm font-medium text-slate-800">
                {loadingPartnerProfile ? (
                  <Skeleton width={70} className="ml-auto" />
                ) : gst ? (
                  `***** ***** ${gst.slice(-5)}`
                ) : (
                  '-'
                )}
              </h4>
            </div>

            {/* Bank Details - Account number */}
            <div className="lg:text-right col-span-1 lg:col-span-1">
              <h3 className="text-stone-500 font-semibold text-xs uppercase mb-1">
                Account number
              </h3>
              <p className="text-sm font-medium text-slate-800">
                {loadingPartnerProfile ? (
                  <Skeleton width={70} className="ml-auto" />
                ) : (
                  accountNumber || '-'
                )}
              </p>
            </div>

            {/* Bank Details - IFSC Code */}
            <div className="lg:text-right col-span-1 lg:col-span-1">
              <h3 className="text-stone-500 font-semibold text-xs uppercase mb-1">IFSC Code</h3>
              <p className="text-sm font-medium text-slate-800">
                {loadingPartnerProfile ? (
                  <Skeleton width={70} className="ml-auto" />
                ) : (
                  accountIfsc || '-'
                )}
              </p>
            </div>
          </div>
        )}
      </div>
      {profileData?.features?.enableInvoices && (
        <>
          {/* Invoices Table */}
          <Tables
            columns={columns}
            rows={rows}
            handleChangePage={() => {}}
            handleChangeRowsPerPage={() => {}}
            handleChangeSort={() => {}}
            handleRowClick={() => {}}
            rowsPerPage={10}
            page={0}
            totalData={rows.length}
            pagination={false}
            disableCursor={true}
          />
        </>
      )}
    </>
  );
};

Profile.displayName = 'Profile';

export default Profile;
